import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';

/**
 * JobReviewModal - Modal for reviewing a cancelled job with blacklist options
 * 
 * Props:
 * - show: boolean - whether modal is visible
 * - onHide: function - called when modal is closed
 * - onSubmit: function - called with review data { starRating, reviewText, isBlacklisted, blacklistReason }
 * - job: object - the job being reviewed (for customer info)
 * - isSubmitting: boolean - whether submission is in progress
 */
export default function JobReviewModal({ show, onHide, onSubmit, job, isSubmitting = false }) {
  const [starRating, setStarRating] = useState(5); // Default 5 stars
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate blacklist reason if blacklist is checked
    if (isBlacklisted && !blacklistReason.trim()) {
      alert('Please provide a reason for blacklisting this customer');
      return;
    }
    
    onSubmit({
      starRating,
      reviewText: reviewText.trim(),
      isBlacklisted,
      blacklistReason: isBlacklisted ? blacklistReason.trim() : ''
    });
  };

  const handleClose = () => {
    // Reset form
    setStarRating(5);
    setReviewText('');
    setIsBlacklisted(false);
    setBlacklistReason('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#6c757d', color: 'white' }}>
        <Modal.Title>⭐ Review Cancelled Job</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {job && (
          <div className="mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <small>
              <strong>{job.customerName}</strong> • {job.customerPhone}<br/>
              {job.from} → {job.to}<br/>
              <span className="text-muted">Cancelled: {job.cancellationReason || 'No reason provided'}</span>
            </small>
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Star Rating Section */}
          <div className="mb-3">
            <h6 className="mb-2">⭐ Customer Review</h6>
            <div className="d-flex align-items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={32}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                  color={(hoverRating || starRating) >= star ? '#ffc107' : '#e4e5e9'}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setStarRating(star)}
                />
              ))}
              <span className="ms-1" style={{ fontWeight: 'bold' }}>
                {starRating}/5
              </span>
            </div>
          </div>

          {/* Optional Review Text */}
          <Form.Group className="mb-3">
            <Form.Label>Review Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience with this customer..."
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {reviewText.length}/500 characters
            </Form.Text>
          </Form.Group>

          {/* Blacklist Section */}
          <div className="mb-2 p-2" style={{ 
            backgroundColor: isBlacklisted ? '#fff3cd' : '#f8f9fa', 
            borderRadius: '6px',
            border: isBlacklisted ? '2px solid #ffc107' : '1px solid #dee2e6'
          }}>
            <Form.Check
              type="checkbox"
              id="blacklistCheckbox"
              checked={isBlacklisted}
              onChange={(e) => setIsBlacklisted(e.target.checked)}
              label={<span style={{ fontWeight: 'bold' }}>⚠️ Blacklist Customer</span>}
            />

            {isBlacklisted && (
              <Form.Group className="mt-2">
                <Form.Label>Reason for blacklisting *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={blacklistReason}
                  onChange={(e) => setBlacklistReason(e.target.value)}
                  placeholder="Please provide detailed reasoning..."
                  maxLength={1000}
                  required={isBlacklisted}
                  style={{ borderColor: isBlacklisted && !blacklistReason ? '#dc3545' : undefined }}
                />
                <Form.Text className="text-muted">
                  {blacklistReason.length}/1000 characters
                </Form.Text>
              </Form.Group>
            )}
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button 
          variant="secondary" 
          size="sm"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || (isBlacklisted && !blacklistReason.trim())}
        >
          {isSubmitting ? (
            <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
          ) : (
            <>⭐ Save Review</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
